import { useEffect, useRef } from 'react';
import { useQueryClient, QueryClient } from 'react-query';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { isAuthFailed } from './useStopSyncOnAuthError';

// Types for the sync connector state
interface SyncConnectorState {
  // Map of query keys to timestamps for when they were last modified
  syncMap: Record<string, number>;
  // Update the sync map for a specific query key
  updateSync: (queryKey: string, timestamp: number) => void;
  // Get the timestamp for a specific query key
  getSyncTimestamp: (queryKey: string) => number | undefined;
  // Invalidate a query key, triggering a refetch
  invalidateQuery: (queryKey: string) => void;
}

// Create a Zustand store for the sync connector with localStorage persistence
export const useSyncConnectorStore = create<SyncConnectorState>()(
  persist(
    (set, get) => ({
      syncMap: {},
      updateSync: (queryKey, timestamp) => {
        const prevTimestamp = get().syncMap[queryKey];

        // Skip update if timestamp is older than what we already have
        if (prevTimestamp && prevTimestamp >= timestamp) {
          return;
        }

        // Ensure timestamp is valid
        const validTimestamp = timestamp && isFinite(timestamp) ? timestamp : Date.now();

        set(state => ({
          syncMap: {
            ...state.syncMap,
            [queryKey]: validTimestamp,
          },
        }));
      },
      getSyncTimestamp: queryKey => get().syncMap[queryKey],
      invalidateQuery: queryKey => {
        const currentTime = Date.now();
        const prevTimestamp = get().syncMap[queryKey];

        // Only update if this timestamp is newer
        if (!prevTimestamp || currentTime > prevTimestamp) {
          set(state => ({
            syncMap: {
              ...state.syncMap,
              [queryKey]: currentTime,
            },
          }));
        }
      },
    }),
    {
      name: 'sync-connector-storage', // unique name for localStorage
    }
  )
);

// Set up interceptor for the React Query client to capture all invalidations
// This should be called once during app initialization
export function setupSyncQueryClientInterceptor(queryClient: QueryClient) {
  // Store the original invalidateQueries method
  const originalInvalidateQueries = queryClient.invalidateQueries;

  // Replace with our custom version that also notifies the sync system
  // @ts-ignore - We're monkey-patching the queryClient
  queryClient.invalidateQueries = function () {
    try {
      // Check for auth failures before proceeding
      if (isAuthFailed) {
        console.log('Skipping invalidateQueries due to auth failure');
        return Promise.resolve();
      }

      // Call the original method
      // @ts-ignore - Using apply with arguments
      const result = originalInvalidateQueries.apply(queryClient, arguments);

      try {
        // Get query key from arguments
        const queryKey = arguments[0];

        // Convert the query key to a format our sync system can use
        if (queryKey) {
          let keyToSync;

          if (typeof queryKey === 'string') {
            keyToSync = [queryKey];
          } else if (Array.isArray(queryKey)) {
            keyToSync = queryKey;
          } else if (queryKey && typeof queryKey === 'object' && 'queryKey' in queryKey) {
            keyToSync = queryKey.queryKey;
          }

          if (keyToSync) {
            const { invalidateQuery } = useSyncConnectorStore.getState();
            const serializedKey = JSON.stringify(keyToSync);
            invalidateQuery(serializedKey);
          }
        }
      } catch (syncError) {
        // Don't let sync issues affect the original invalidation
      }

      return result;
    } catch (queryError) {
      // Try to call original directly as a fallback
      try {
        // @ts-ignore
        return originalInvalidateQueries.apply(queryClient, arguments);
      } catch {
        // Re-throw the original error to maintain expected behavior
        throw queryError;
      }
    }
  };

  return () => {
    // Cleanup function to restore original method if needed
    // @ts-ignore - Restoring the original method
    queryClient.invalidateQueries = originalInvalidateQueries;
  };
}

// Helper function to convert query key array to string for storage
const serializeQueryKey = (queryKey: unknown[]): string => {
  return JSON.stringify(queryKey);
};

// Batch updates to reduce processing overhead
class UpdateBatcher {
  private updates: Map<string, { queryKey: unknown[]; timestamp: number }> = new Map();
  private timeoutId: ReturnType<typeof setTimeout> | null = null;
  private processing = false;
  private rafId: number | null = null;

  constructor(private processUpdate: (queryKey: unknown[], timestamp: number) => void) {}

  add(queryKey: unknown[], timestamp: number) {
    // Skip processing if auth has failed
    if (isAuthFailed) {
      console.log('Skipping update batching due to auth failure');
      return;
    }

    const key = serializeQueryKey(queryKey);
    const existing = this.updates.get(key);

    // Only update if timestamp is newer
    if (!existing || existing.timestamp < timestamp) {
      this.updates.set(key, { queryKey, timestamp });
    }

    // Check if this is an assembly update for priority processing
    const isAssemblyUpdate =
      Array.isArray(queryKey) && queryKey.length > 0 && queryKey[0] === 'countedAssemblies';

    if (isAssemblyUpdate) {
      // Process assembly updates with higher priority
      this.cancelScheduledProcessing();
      this.scheduleAsap();
    } else if (!this.timeoutId && !this.rafId) {
      // Schedule normal processing if not already scheduled
      this.scheduleProcess();
    }
  }

  private cancelScheduledProcessing() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  private scheduleAsap() {
    if (this.processing) return;

    // Use requestAnimationFrame for better performance than immediate timeout
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.processUpdates(true); // Process assembly updates first
    });
  }

  private scheduleProcess() {
    if (this.processing || this.timeoutId || this.rafId) return;

    // Use a longer delay for non-priority updates
    this.timeoutId = setTimeout(() => {
      this.timeoutId = null;
      this.processUpdates(false);
    }, 100);
  }

  private processUpdates(prioritizeAssembly = false) {
    // Skip processing if auth has failed
    if (isAuthFailed) {
      console.log('Skipping update processing due to auth failure');
      this.updates.clear();
      this.processing = false;
      return;
    }

    if (this.processing || this.updates.size === 0) return;

    this.processing = true;

    // Split updates into assembly and non-assembly
    const updates = Array.from(this.updates.values());
    const assemblyUpdates = updates.filter(
      update => Array.isArray(update.queryKey) && update.queryKey[0] === 'countedAssemblies'
    );
    const otherUpdates = updates.filter(
      update => !Array.isArray(update.queryKey) || update.queryKey[0] !== 'countedAssemblies'
    );

    // Process the right updates based on priority
    const updatesToProcess = prioritizeAssembly
      ? assemblyUpdates.length > 0
        ? assemblyUpdates
        : otherUpdates.slice(0, 3)
      : otherUpdates.slice(0, 3);

    if (updatesToProcess.length > 0) {
      // Process in microtasks to avoid long tasks
      Promise.resolve().then(() => {
        updatesToProcess.forEach(update => {
          this.processUpdate(update.queryKey, update.timestamp);
          this.updates.delete(serializeQueryKey(update.queryKey));
        });

        this.processing = false;

        // If there are still updates to process, schedule next batch
        if (this.updates.size > 0) {
          // Check if we still have assembly updates
          const hasAssemblyUpdates = Array.from(this.updates.values()).some(
            update => Array.isArray(update.queryKey) && update.queryKey[0] === 'countedAssemblies'
          );

          if (hasAssemblyUpdates) {
            this.scheduleAsap();
          } else {
            this.scheduleProcess();
          }
        }
      });
    } else {
      this.processing = false;
    }
  }

  clear() {
    this.updates.clear();
    this.cancelScheduledProcessing();
    this.processing = false;
  }
}

/**
 * Hook that listens for changes to specified query keys and refetches when
 * they are invalidated by another screen/component
 *
 * @param watchedQueryKeys Array of query keys to watch for changes
 * @param refetchInterval Optional interval in milliseconds to check for updates (default: 1000ms)
 * @param disableAutoSync Optional flag to disable automatic syncing (default: false)
 */
export const useSyncWatcher = (
  watchedQueryKeys: unknown[][],
  refetchInterval = 1000,
  disableAutoSync = false
) => {
  const queryClient = useQueryClient();
  const { getSyncTimestamp } = useSyncConnectorStore();

  const processedTimestamps = useRef<Record<string, number>>({});
  const batcherRef = useRef<UpdateBatcher | null>(null);
  const lastCheckRef = useRef(Date.now());

  // Initialize the batcher
  useEffect(() => {
    // Skip if automatic syncing is disabled
    if (disableAutoSync) {
      return;
    }

    const handleUpdate = (queryKey: unknown[], timestamp: number): void => {
      const serializedKey = serializeQueryKey(queryKey);

      if (processedTimestamps.current[serializedKey] >= timestamp) {
        return;
      }

      processedTimestamps.current[serializedKey] = timestamp;

      // Minimize work in setTimeout by just scheduling the invalidation
      Promise.resolve().then(() => {
        queryClient.invalidateQueries(queryKey);
      });
    };

    batcherRef.current = new UpdateBatcher(handleUpdate);

    return () => {
      if (batcherRef.current) {
        batcherRef.current.clear();
      }
    };
  }, [queryClient, disableAutoSync]);

  // Check if we're watching any assembly keys
  const hasAssemblyKeys = watchedQueryKeys.some(
    key => Array.isArray(key) && key.length > 0 && key[0] === 'countedAssemblies'
  );

  // Use different intervals for different types of updates
  const effectiveInterval = hasAssemblyKeys ? 200 : Math.max(refetchInterval, 2000);

  // Listen for localStorage events
  useEffect(() => {
    // Skip if automatic syncing is disabled
    if (disableAutoSync) {
      return;
    }

    // Lighter throttle function that minimizes timer work
    const processStorageEvent = (e: StorageEvent) => {
      if (e.key !== 'sync-connector-storage' || !batcherRef.current) return;

      try {
        const data = JSON.parse(e.newValue || '{}');
        const syncMap = data?.state?.syncMap;

        if (syncMap) {
          // Process assembly keys first
          const assemblyKeys = watchedQueryKeys.filter(
            key => Array.isArray(key) && key.length > 0 && key[0] === 'countedAssemblies'
          );

          // Process assembly keys immediately
          if (assemblyKeys.length > 0) {
            assemblyKeys.forEach(queryKey => {
              const serializedKey = serializeQueryKey(queryKey);
              const timestamp = syncMap[serializedKey];

              if (
                timestamp &&
                (!processedTimestamps.current[serializedKey] ||
                  processedTimestamps.current[serializedKey] < timestamp)
              ) {
                batcherRef.current?.add(queryKey, timestamp);
              }
            });
          }

          // Process other keys with lower priority
          const otherKeys = watchedQueryKeys.filter(
            key => !Array.isArray(key) || key.length === 0 || key[0] !== 'countedAssemblies'
          );

          if (otherKeys.length > 0) {
            // Use RAF to schedule this work during idle time
            requestAnimationFrame(() => {
              otherKeys.forEach(queryKey => {
                const serializedKey = serializeQueryKey(queryKey);
                const timestamp = syncMap[serializedKey];

                if (
                  timestamp &&
                  (!processedTimestamps.current[serializedKey] ||
                    processedTimestamps.current[serializedKey] < timestamp)
                ) {
                  batcherRef.current?.add(queryKey, timestamp);
                }
              });
            });
          }
        }
      } catch (error) {
        // Silent error handling
      }
    };

    window.addEventListener('storage', processStorageEvent);
    return () => window.removeEventListener('storage', processStorageEvent);
  }, [watchedQueryKeys, disableAutoSync]);

  // Use RAF for polling to avoid setTimeout violations
  useEffect(() => {
    // Skip if automatic syncing is disabled
    if (disableAutoSync) {
      return;
    }

    let rafId: number | null = null;

    const checkForUpdates = () => {
      // Skip if auth has failed
      if (isAuthFailed) {
        console.log('Skipping sync updates due to auth failure');
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }
        return;
      }

      const now = Date.now();
      const batcher = batcherRef.current;

      // Only process if enough time has passed and we have a batcher
      if (now - lastCheckRef.current >= effectiveInterval && batcher) {
        lastCheckRef.current = now;

        // First check assembly keys
        const assemblyKeys = watchedQueryKeys.filter(
          key => Array.isArray(key) && key.length > 0 && key[0] === 'countedAssemblies'
        );

        assemblyKeys.forEach(queryKey => {
          const serializedKey = serializeQueryKey(queryKey);
          const lastSyncTimestamp = getSyncTimestamp(serializedKey);

          if (
            lastSyncTimestamp &&
            (!processedTimestamps.current[serializedKey] ||
              processedTimestamps.current[serializedKey] < lastSyncTimestamp)
          ) {
            batcher.add(queryKey, lastSyncTimestamp);
          }
        });

        // Then check other keys with lower priority if we have time
        const otherKeys = watchedQueryKeys.filter(
          key => !Array.isArray(key) || key.length === 0 || key[0] !== 'countedAssemblies'
        );

        if (otherKeys.length > 0 && performance.now() - now < 10) {
          otherKeys.forEach(queryKey => {
            const serializedKey = serializeQueryKey(queryKey);
            const lastSyncTimestamp = getSyncTimestamp(serializedKey);

            if (
              lastSyncTimestamp &&
              (!processedTimestamps.current[serializedKey] ||
                processedTimestamps.current[serializedKey] < lastSyncTimestamp)
            ) {
              batcher.add(queryKey, lastSyncTimestamp);
            }
          });
        }
      }

      // Schedule next check
      rafId = requestAnimationFrame(checkForUpdates);
    };

    // Start the checking cycle
    rafId = requestAnimationFrame(checkForUpdates);

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [watchedQueryKeys, getSyncTimestamp, effectiveInterval, disableAutoSync]);
};

/**
 * Hook that triggers invalidation of a query key across all screens/components
 * that are watching that key
 */
export const useSyncInvalidation = () => {
  const { invalidateQuery } = useSyncConnectorStore();
  const queryClient = useQueryClient();

  return {
    /**
     * Invalidate a query key across all screens
     * @param queryKey The query key to invalidate
     * @returns A promise that resolves when invalidation is complete or rejects on failure
     */
    invalidateSync: async (queryKey: unknown[]) => {
      // Check for auth failures first
      if (isAuthFailed) {
        const error = new Error('Authentication failed, cannot invalidate query');
        error.name = 'AuthenticationError';
        console.warn(`Auth failed - skipping sync invalidation for: ${JSON.stringify(queryKey)}`);

        // Try a regular refetch as a fallback (which may also fail, but might work in some cases)
        try {
          // This might fail too if auth is completely broken
          await queryClient.refetchQueries(queryKey, { exact: true });
        } catch (refetchError) {
          // Just suppress the error, we've already warned
        }

        // Return a rejected promise so the caller can handle it
        return Promise.reject(error);
      }

      // Proceed with normal invalidation
      try {
        const serializedKey = serializeQueryKey(queryKey);
        invalidateQuery(serializedKey);
        return Promise.resolve();
      } catch (error) {
        console.error(`Error in sync invalidation for: ${JSON.stringify(queryKey)}`, error);
        return Promise.reject(error);
      }
    },
  };
};

/**
 * Wraps a mutation with sync invalidation to automatically
 * invalidate specified query keys across all screens when the mutation succeeds
 *
 * @param mutationFn The mutation function to wrap
 * @param queryKeysToInvalidate Array of query keys to invalidate on success
 */
export const withSyncInvalidation = <T, TData>(
  mutationFn: (data: T) => Promise<TData>,
  queryKeysToInvalidate: unknown[][]
) => {
  return async (data: T): Promise<TData> => {
    const { invalidateSync } = useSyncInvalidation();
    try {
      const result = await mutationFn(data);

      // On successful mutation, invalidate all specified query keys
      queryKeysToInvalidate.forEach(queryKey => {
        invalidateSync(queryKey);
      });

      return result;
    } catch (error) {
      // Just rethrow the error, let the calling code handle it
      throw error;
    }
  };
};
